/**
 * Sysadmin broadcast - salje poruke tenantima (ili svim).
 * Ispod forme: istorija poslatih sa read-status indikatorom.
 */

import { useEffect, useState, useCallback } from 'react';
import { Send, AlertTriangle, Loader2, Check, Users, Globe } from 'lucide-react';
import { api } from '../../api/client';

type Tenant = { id: string; name: string; taxId: string };
type SentNotification = {
  id: string;
  tenantId: string;
  userId: string | null;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  sentBy: string;
  createdAt: string;
  readAt: string | null;
};

const severityStyle: Record<string, { bg: string; color: string; label: string }> = {
  info:     { bg: 'rgba(37,99,235,0.12)',  color: '#2563eb', label: 'Info' },
  warning:  { bg: 'rgba(202,138,4,0.15)',  color: '#ca8a04', label: 'Upozorenje' },
  critical: { bg: 'rgba(220,38,38,0.15)',  color: '#dc2626', label: 'Hitno' },
};

export function NotificationsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [history, setHistory] = useState<SentNotification[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [target, setTarget] = useState<'all' | 'tenants'>('all');
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('info');
  const [actionUrl, setActionUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const rows = await api.get<SentNotification[]>('/admin/notifications?limit=100');
      setHistory(rows);
    } catch {}
    finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => {
    api.get<Tenant[]>('/admin/notifications/tenants').then(setTenants).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const toggleTenant = (id: string) => {
    setSelectedTenantIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) { setError('Naslov i poruka su obavezni'); return; }
    if (target === 'tenants' && selectedTenantIds.length === 0) { setError('Izaberi bar jednog klijenta'); return; }

    setSending(true); setError(null); setNotice(null);
    try {
      const res = await api.post<{ ok: boolean; count: number }>('/admin/notifications', {
        target, tenantIds: target === 'tenants' ? selectedTenantIds : undefined,
        title, message, severity,
        actionUrl: actionUrl.trim() || undefined,
        expiresAt: expiresAt || undefined,
      });
      setNotice(`Poslato ${res.count} obaveštenja.`);
      setTitle(''); setMessage(''); setActionUrl(''); setExpiresAt(''); setSelectedTenantIds([]);
      loadHistory();
    } catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setSending(false); }
  };

  const tenantName = (id: string) => tenants.find((t) => t.id === id)?.name || id.slice(0, 8);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-strong)', margin: 0 }}>Obaveštenja</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>
          Pošaljite poruke klijentima (billing podsetnici idu automatski - ovde su manualne poruke).
        </p>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Form */}
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)', marginBottom: 12 }}>Nova poruka</div>

          <Label>Cilj</Label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button onClick={() => setTarget('all')} style={{ ...tabBtn, ...(target === 'all' ? tabBtnActive : {}) }}>
              <Globe size={13} /> Svi klijenti
            </button>
            <button onClick={() => setTarget('tenants')} style={{ ...tabBtn, ...(target === 'tenants' ? tabBtnActive : {}) }}>
              <Users size={13} /> Izabrani ({selectedTenantIds.length})
            </button>
          </div>

          {target === 'tenants' && (
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, padding: 6, marginBottom: 12 }}>
              {tenants.map((t) => (
                <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedTenantIds.includes(t.id)} onChange={() => toggleTenant(t.id)} />
                  <span style={{ color: 'var(--color-text-strong)' }}>{t.name}</span>
                  <span style={{ color: 'var(--color-text-muted)', fontFamily: 'monospace', marginLeft: 'auto' }}>{t.taxId}</span>
                </label>
              ))}
            </div>
          )}

          <Label>Naslov *</Label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={input} placeholder="npr. Održavanje sistema" />

          <Label>Poruka *</Label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} style={{ ...input, minHeight: 100 }} placeholder="Tekst poruke..." />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Label>Ozbiljnost</Label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value as any)} style={input}>
                <option value="info">Info</option>
                <option value="warning">Upozorenje</option>
                <option value="critical">Hitno</option>
              </select>
            </div>
            <div>
              <Label>Ističe (opciono)</Label>
              <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} style={input} />
            </div>
          </div>

          <Label>Link (opciono)</Label>
          <input value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} style={input} placeholder="/billing" />

          <button onClick={handleSend} disabled={sending} style={{ ...btnPrimary, marginTop: 14 }}>
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Pošalji
          </button>
        </div>

        {/* History */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Istorija slanja</div>
            <div style={{ flex: 1 }} />
            <button onClick={loadHistory} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', fontSize: 12, cursor: 'pointer' }}>
              Osveži
            </button>
          </div>
          {loadingHistory && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Učitavanje...</div>}
          {!loadingHistory && history.length === 0 && (
            <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nema poslatih obaveštenja.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
            {history.map((n) => {
              const sev = severityStyle[n.severity] || severityStyle.info;
              return (
                <div key={n.id} style={{ padding: 10, border: '1px solid var(--color-border)', borderRadius: 6, borderLeft: `3px solid ${sev.color}` }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: sev.bg, color: sev.color, fontWeight: 700 }}>{sev.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>{n.title}</span>
                    <div style={{ flex: 1 }} />
                    {n.readAt ? (
                      <span style={{ fontSize: 10, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}><Check size={10} /> pročitano</span>
                    ) : (
                      <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>nepročitano</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>
                    {tenantName(n.tenantId)} · {new Date(n.createdAt).toLocaleString('sr-Latn-RS')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, marginTop: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{children}</label>;
}

const cardStyle: React.CSSProperties = { background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 };
const input: React.CSSProperties = { padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const tabBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 12, cursor: 'pointer' };
const tabBtnActive: React.CSSProperties = { background: 'var(--color-primary)', color: '#fff', borderColor: 'var(--color-primary)' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#dc2626', fontSize: 13, marginBottom: 14 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#16a34a', fontSize: 13, marginBottom: 14 };
